import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, ImageUp, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import type { Listing } from "@/types";

type Kind = "letter" | "a4" | "cover" | "previews";
const ITEMS: { kind: Kind; label: string; accept: string; hint: string }[] = [
  { kind: "letter", label: "US Letter PDF", accept: "application/pdf", hint: "What buyers download" },
  { kind: "a4", label: "A4 PDF (optional)", accept: "application/pdf", hint: "For buyers outside the US" },
  { kind: "cover", label: "Cover image", accept: "image/jpeg,image/png,image/webp", hint: "Shop card + first photo" },
  { kind: "previews", label: "Preview images (up to 4)", accept: "image/jpeg,image/png,image/webp", hint: "Replaces the page previews" },
];

export function CustomDesignPanel({ listing, onChange, disabled }: { listing: Listing; onChange: (l: Listing) => void; disabled: boolean }) {
  const [busy, setBusy] = useState<Kind | "restore" | "">("");
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  const upload = async (kind: Kind, files: FileList | null) => {
    if (!files?.length) return;
    const fd = new FormData();
    const url = kind === "cover" ? "cover" : kind === "previews" ? "previews" : `pdf?paper=${kind}`;
    if (kind === "previews") Array.from(files).slice(0, 4).forEach((f) => fd.append("files", f));
    else fd.append("file", files[0]);
    setBusy(kind);
    try {
      const { data } = await api.post(`/listings/${listing.id}/upload/${url}`, fd);
      onChange(data);
      toast.success("Uploaded. Buyers and the shop now see your version.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); const el = inputs.current[kind]; if (el) el.value = ""; }
  };

  const restore = async () => {
    if (!window.confirm("Switch back to the generated design and files?")) return;
    setBusy("restore");
    try { const { data } = await api.post(`/listings/${listing.id}/restore-generated`); onChange(data); toast.success("Back to the generated design"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="paper-card p-5" data-testid="custom-design-panel">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <h2 className="font-display text-lg font-extrabold text-[#23324A]">Your own design</h2>
        {listing.custom_design && <span className="rounded-full bg-[#DCEFE3] px-3 py-0.5 text-xs font-bold text-[#23324A]" data-testid="custom-design-badge">Using your upload</span>}
      </div>
      <p className="mb-4 text-sm text-[#23324A]/70">Upload your redesign. It replaces what buyers download and what the shop shows. The generated version is kept as a backup.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {ITEMS.map((it) => (
          <label key={it.kind} className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 border-dashed border-[#23324A]/30 p-3 transition-colors hover:border-[#23324A] ${disabled || busy ? "pointer-events-none opacity-60" : ""}`} data-testid={`upload-${it.kind}-label`}>
            {it.kind === "letter" || it.kind === "a4" ? <FileUp className="mt-0.5 h-5 w-5 shrink-0 text-[#23324A]" /> : <ImageUp className="mt-0.5 h-5 w-5 shrink-0 text-[#23324A]" />}
            <span>
              <span className="block text-sm font-bold text-[#23324A]">{busy === it.kind ? "Uploading…" : it.label}</span>
              <span className="block text-xs text-[#23324A]/60">{it.hint}</span>
            </span>
            <input ref={(el) => { inputs.current[it.kind] = el; }} type="file" accept={it.accept} multiple={it.kind === "previews"} className="hidden"
              onChange={(e) => upload(it.kind, e.target.files)} data-testid={`upload-${it.kind}-input`} />
          </label>
        ))}
      </div>
      {listing.custom_design && (
        <button className="btn-ghost mt-4" disabled={!!busy} onClick={restore} data-testid="restore-generated-btn"><RotateCcw className="h-4 w-4" /> Switch back to generated design</button>
      )}
    </div>
  );
}
